import { useUserStore } from '@/store/user'
// 有意深链不走 @/utils 桶：桶文件 import 了 @/pages.json 与整套导航代码，http 层保持轻依赖
import { platform } from '@/utils/platform'

export type CustomRequestOptions = UniApp.RequestOptions & {
  query?: Record<string, any>
  /** 出错时是否隐藏错误提示 */
  hideErrorToast?: boolean
} & IUniUploadFileOptions // 兼容 uni.uploadFile 参数类型

/**
 * 将 query 对象序列化为 queryString（对齐 qs.stringify 默认行为）。
 * 自己实现而非用 qs：qs 会拖入 side-channel 依赖，小程序打包后运行时报错。
 * - undefined 值跳过，null 序列化为空串
 * - 数组用 `key[i]=v` 形式
 */
function stringifyQuery(query: Record<string, any>): string {
  const enc = encodeURIComponent
  const parts: string[] = []
  const add = (key: string, value: any) => {
    if (value === undefined)
      return
    parts.push(`${enc(key)}=${enc(value ?? '')}`)
  }
  for (const key in query) {
    const value = query[key]
    if (Array.isArray(value))
      value.forEach((v, i) => add(`${key}[${i}]`, v))
    else add(key, value)
  }
  return parts.join('&')
}

// 请求基准地址
const baseUrl = import.meta.env.VITE_SERVER_BASEURL

// 请求拦截器：统一拼接 baseUrl / query、注入平台标识与鉴权头
const httpInterceptor = {
  invoke(options: CustomRequestOptions) {
    // 支持通过 query 参数配置 queryString
    if (options.query) {
      const queryStr = stringifyQuery(options.query)
      if (queryStr) {
        options.url += options.url.includes('?') ? `&${queryStr}` : `?${queryStr}`
      }
    }
    // 非 http 开头需拼接基础地址（如需对接多个后端服务，可在此按前缀分流）
    if (!options.url.startsWith('http')) {
      options.url = baseUrl + options.url
    }
    // 请求超时默认 30s（调用方已指定则不覆盖）
    options.timeout ??= 30000
    // 平台标识，告诉后端来源
    options.header = {
      platform,
      ...options.header,
    }
    // 鉴权：注入后端约定的 Token 头
    const userStore = useUserStore()
    const { token } = userStore.loginInfo
    if (token) {
      options.header.Token = token
    }
    // openId：注入后端约定的 ownId 头（登录成功后经 user store refreshOpenId 获取，取不到传空串）
    options.header.ownId = userStore.openId || ''
  },
}

export const requestInterceptor = {
  install() {
    // 拦截 request 请求
    uni.addInterceptor('request', httpInterceptor)
    // 拦截 uploadFile 文件上传
    uni.addInterceptor('uploadFile', httpInterceptor)
  },
}
